import {
  AREA_UNITS,
  OPERATIONS,
  PROPERTY_FIELD_LABELS,
  PROPERTY_TYPES,
  formatDate,
  formatMoney,
  formatPhone,
  type PropertyField,
} from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import { Mail, MessageCircle, Phone, Rotate3d, Video } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';

import { Alert } from '../../components/ui/alert.tsx';
import { buttonVariants } from '../../components/ui/button.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, ApiError } from '../../lib/api.ts';
import { cn } from '../../lib/cn.ts';
import { whatsappLink } from '../../lib/contacts-api.ts';
import { priceLabel, type PublicListing } from '../../lib/properties-api.ts';

/**
 * What a client sees from a share link (no account): photos, price, zone and
 * characteristics, with the agent's (or agency's) contact. Never the exact address
 * or private data: the API only returns public fields.
 */
export function SharedListingPage() {
  const { token = '' } = useParams();
  const listing = useQuery({
    queryKey: ['public-listing', token],
    queryFn: () => api<PublicListing>(`/public/listings/${encodeURIComponent(token)}`),
    retry: false,
    staleTime: 5 * 60_000,
  });
  return (
    <main className="mx-auto min-h-screen max-w-3xl space-y-5 bg-background px-4 py-6">
      {listing.isPending ? (
        <Loading />
      ) : listing.error ? (
        <div className="space-y-2 py-16 text-center">
          <h1 className="text-xl font-semibold">Este enlace ya no está disponible</h1>
          <p className="text-muted-foreground">
            {listing.error instanceof ApiError && listing.error.status === 404
              ? 'Puede que haya vencido o que la inmobiliaria lo haya retirado. Pide uno nuevo a tu agente.'
              : 'No pudimos cargar la propiedad. Intenta de nuevo en unos minutos.'}
          </p>
        </div>
      ) : (
        <Listing listing={listing.data} />
      )}
    </main>
  );
}

function Listing({ listing: l }: { listing: PublicListing }) {
  const [current, setCurrent] = useState(0);
  const photo = l.media[current];
  const fields = (PROPERTY_TYPES[l.type].fields as readonly PropertyField[]).filter(
    (f) => l.characteristics[f] !== null && f !== 'yearBuilt',
  );
  const unitOf = (field: 'builtArea' | 'lotArea') =>
    AREA_UNITS[field === 'lotArea' ? l.lotAreaUnit : l.builtAreaUnit];
  const message = `Hola ${l.contact.name}, me interesa la propiedad ${l.code}: ${l.title}.`;

  return (
    <>
      <header className="space-y-1">
        <p className="text-sm font-medium text-primary">{l.agency.name}</p>
        <h1 className="text-2xl font-semibold">{l.title}</h1>
        <p className="text-muted-foreground">
          {[l.neighborhood, `${l.district}, ${l.municipality}`].filter(Boolean).join(' · ')}
        </p>
        <p className="text-xl font-semibold tabular-nums">{priceLabel(l, (n) => formatMoney(n))}</p>
        <p className="text-sm text-muted-foreground">
          {PROPERTY_TYPES[l.type].label} en {OPERATIONS[l.operation].toLowerCase()} · Código{' '}
          <span className="font-mono">{l.code}</span>
        </p>
      </header>

      {!l.available && (
        <Alert tone="warning" title="Propiedad no disponible">
          Ya no está disponible. Tu agente puede mostrarte opciones parecidas.
        </Alert>
      )}

      {photo && (
        <section aria-label="Fotos" className="space-y-2">
          <img
            src={photo.largeUrl}
            alt={photo.caption ?? `Foto ${String(current + 1)} de ${String(l.media.length)}`}
            className="aspect-[4/3] w-full rounded-xl bg-muted object-cover"
          />
          {l.media.length > 1 && (
            <ul className="flex gap-2 overflow-x-auto pb-1">
              {l.media.map((m, i) => (
                <li key={m.id} className="shrink-0">
                  <button
                    type="button"
                    aria-label={`Ver foto ${String(i + 1)}`}
                    aria-current={i === current}
                    onClick={() => {
                      setCurrent(i);
                    }}
                    className={cn(
                      'block h-16 w-20 overflow-hidden rounded-lg border-2',
                      i === current ? 'border-primary' : 'border-transparent',
                    )}
                  >
                    <img
                      src={m.thumbUrl}
                      alt=""
                      className="size-full object-cover"
                      loading="lazy"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {fields.length > 0 && (
        <section className="rounded-xl border border-border bg-surface p-4">
          <h2 className="mb-2 font-semibold">Características</h2>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
            {fields.map((field) => {
              const value = l.characteristics[field] ?? 0;
              return (
                <div key={field}>
                  <dt className="text-muted-foreground">{PROPERTY_FIELD_LABELS[field]}</dt>
                  <dd className="font-medium">
                    {field === 'builtArea' || field === 'lotArea'
                      ? `${String(value)} ${unitOf(field)}`
                      : field === 'maintenanceFee'
                        ? formatMoney(value)
                        : String(value)}
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>
      )}

      {l.description && (
        <section className="space-y-1">
          <h2 className="font-semibold">Descripción</h2>
          <p className="whitespace-pre-line">{l.description}</p>
        </section>
      )}

      {(l.videoUrl ?? l.virtualTourUrl) && (
        <div className="flex flex-wrap gap-2">
          {l.videoUrl && (
            <a
              href={l.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: 'secondary' })}
            >
              <Video /> Ver video
            </a>
          )}
          {l.virtualTourUrl && (
            <a
              href={l.virtualTourUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: 'secondary' })}
            >
              <Rotate3d /> Recorrido virtual
            </a>
          )}
        </div>
      )}

      <section className="space-y-3 rounded-xl border border-border bg-surface p-4">
        <div>
          <h2 className="font-semibold">¿Te interesa?</h2>
          <p className="text-sm text-muted-foreground">
            {l.contact.name} · {l.agency.name}
            {l.contact.phone ? ` · ${formatPhone(l.contact.phone)}` : ''}
          </p>
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          {l.contact.phone && (
            <a
              href={whatsappLink(l.contact.phone, message)}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: 'brand' })}
            >
              <MessageCircle /> WhatsApp
            </a>
          )}
          {l.contact.phone && (
            <a href={`tel:${l.contact.phone}`} className={buttonVariants({ variant: 'secondary' })}>
              <Phone /> Llamar
            </a>
          )}
          <a
            href={`mailto:${l.contact.email}?subject=${encodeURIComponent(`Propiedad ${l.code}`)}&body=${encodeURIComponent(message)}`}
            className={buttonVariants({ variant: 'secondary' })}
          >
            <Mail /> Correo
          </a>
        </div>
      </section>

      <p className="pb-4 text-center text-xs text-muted-foreground">
        Ubicación aproximada: la dirección exacta se comparte al coordinar la visita. Este enlace
        vence el {formatDate(l.expiresAt)}.
      </p>
    </>
  );
}
